// @ts-check
import { createAsyncThunk } from '@reduxjs/toolkit';
import { bindActionCreators } from 'redux';

import { createAppSlice } from '@exp/redux/createAppSlice';
import { calculateFreeShippingInfo } from '@exp/components/products/freeShippingUtils';
import { fetchMenu } from '@exp/components/menu/api';
import { flattenMenuElements } from '@exp/components/menu/menuUtils';
import {
  MenuElementType,
  SECONDARY_MENU_KEY,
  TRENDING_SEARCHES_MENU_KEY,
} from '@exp/components/menu/constants';

import {
  addSaveForLater,
  claimCoupon,
  deleteCartItem,
  deleteSaveForLater,
  fetchGroupsWithCoupons,
  getCart,
  getCartSetShipInfo,
  getCartSuggestions,
  getCartSuggestionsByProduct,
  getCoupon,
  getSaveForLater,
  moveCartToSaved,
  moveSavedToCart,
  updateCartItem,
} from '../js/common/api';
import { fetchPromoStores } from '../promoBanner/promoBannerReducer';
import { fetchStoreCampaigns } from '../product/productDetailsActions';
import { addOrUpdateObject } from '../js/common/utils';

/** @import ProductTypes from '@exp/components/products/productTypes' */
/** @import CartTypes from './cartTypes' */
/** @import { CartGroupType } from './cartsContainerPropTypes' */
/** @import { ShipInfoType } from '../sharedComponents/checkoutPropTypes' */

import { ERROR_CODE as ERROR } from '../js/common/constants';

const initialState = {
  appliedCoupons: [],
  /** @type {Record<number, ProductTypes.FreeShipInfo>} - precomputed freeShipInfo by cartId */
  cartFreeShipInfos: null,
  cartItemErrors: [],
  carts: {},
  cartsInfoIsStale: false,
  cartsInformation: null,
  commandeered: null,
  couponFetch: false,
  couponOffers: [],
  coupons: [],
  couponTerms: {},
  /** @type {CartGroupType[]} */
  groupedCarts: null,
  groupedCartsRequestId: null,
  lastUpdateTime: null,
  /** @type {Record<number, number>} - number of carts by orgId */
  orgIdCartCount: {},
  pendingCartAdd: null,
  pendingDeletes: {},
  pendingItemDeletes: {},
  pendingSaved: {},
  pendingSkuUpdates: {},
  pendingUpdates: {},
  prevCoupons: [],
  prevSelectedPrizes: [],
  prizes: [],
  fetchPending: false,
  /** @type {number[][]} - groups of SAD cartIds */
  sadCartGroups: [],
  saved: {},
  /** @type {CartTypes.MenuLink[] | null} */
  secondaryLinks: null,
  selectedPrizes: [],
  /** @type {ProductTypes.Address} - user's known shipping address / postal code */
  shippingAddress: null,
  storesCampaignContents: {},
  /** @type {any[]} */
  suggestions: [],
  /** @type {CartTypes.MenuLink[] | null} */
  trendingSearches: null,
  unknownError: false,
};

const SEARCH_ROUTE = '/home/search/?searchText=';

const toMenuLinks = (elements) =>
  (flattenMenuElements(elements) ?? [])
    .filter(({ label }) => label)
    .map(({ elemId, label, type, url }) => ({
      elemId,
      label,
      type,
      url: url || `${SEARCH_ROUTE}${encodeURIComponent(label)}`,
    }));

const cartSlice = createAppSlice({
  name: 'cart',
  initialState,
  reducers: (create) => ({
    fetchCart: create.asyncThunk(
      (/** @type {string} */ cartId) => getCart(cartId),
      {
        pending: (state) => { state.fetchPending = true; },
        fulfilled: (state, action) => {
          state.fetchPending = false;
          state.carts = addOrUpdateObject(
            { ...state.carts }, action.payload.data.id, action.payload.data,
          );
        },
        rejected: (state) => { state.fetchPending = false; },
      },
    ),

    fetchCartSuggestions: create.asyncThunk(
      () => getCartSuggestions(),
      {
        fulfilled: (state, action) => {
          state.suggestions = action.payload.data?.resultItems;
        },
      },
    ),

    fetchCartSuggestionsByProduct: create.asyncThunk(
      (/** @type {{ orgId: string, productCode: string }} */ { orgId, productCode }) =>
        getCartSuggestionsByProduct(orgId, productCode),
      {},
    ),

    fetchSecondaryMenu: create.asyncThunk(
      () => fetchMenu({ menuKey: SECONDARY_MENU_KEY }),
      {
        fulfilled: (state, action) => {
          state.secondaryLinks = toMenuLinks(action.payload.data?.elements)
            .filter(({ type }) => type === MenuElementType.TRAIT_PER_DEAL);
        },
        rejected: (state) => { state.secondaryLinks = []; },
      },
    ),

    fetchTrendingSearches: create.asyncThunk(
      () => fetchMenu({ menuKey: TRENDING_SEARCHES_MENU_KEY }),
      {
        fulfilled: (state, action) => {
          state.trendingSearches = toMenuLinks(action.payload.data?.elements);
        },
        rejected: (state) => { state.trendingSearches = []; },
      },
    ),

    fetchGroupedCarts: create.asyncThunk(
      (/** @type {any[]} */ appliedCoupons) => fetchGroupsWithCoupons({ coupons: appliedCoupons }),
      {
        pending: (state, action) => {
          state.groupedCartsRequestId = action.meta.requestId;
        },
        fulfilled: (state, action) => {
          // ignore the response if it doesn't match the latest requestId.
          // this can happen if an earlier request finally resolves after a later request
          if (action.meta.requestId !== state.groupedCartsRequestId) {
            return undefined;
          }

          const {
            payload: {
              data: { carts: cartGroups, couponOffers = [], coupons, prizes, shippingAddress },
            },
          } = action;

          couponOffers.unshift(...state.couponOffers.filter((offer) => offer.claimed));

          const couponTerms = coupons
            .reduce((terms, { id, termsConditions }) => ({
              ...terms, [id]: termsConditions,
            }), state.couponTerms);

          const orgIdCartCount = new Map();
          const cartItemErrors = [];

          const groupedCarts = cartGroups.map((cartGroup) => {
            const nonEmptyCarts = cartGroup.carts.filter(({
              cartItems, errors,
              organizationAvatarKey, organizationId, organizationImageKey,
              organizationImageType, organizationKey, storeId,
            }) => {
              errors.forEach((error) => {
                const errorDetails = error.split('::');
                if (errorDetails.length > 1 && errorDetails[0] === ERROR.COUPON_UNCLAIMED) {
                  const itemIndex = cartItems.findIndex((item) => item.id === +errorDetails[1]);
                  if (itemIndex > -1) {
                    cartItemErrors.push({
                      cart: {
                        organizationAvatarKey,
                        organizationId,
                        organizationImageKey,
                        organizationImageType,
                        organizationKey,
                        storeId,
                      },
                      errorCode: errorDetails[0],
                      item: cartItems.splice(itemIndex, 1)[0],
                    });
                  }
                }
              });

              const nonEmpty = !!cartItems?.length;
              if (nonEmpty) {
                orgIdCartCount.set(organizationId, (orgIdCartCount.get(organizationId) || 0) + 1);
              }
              return nonEmpty;
            });

            return { ...cartGroup, carts: nonEmptyCarts };
          }).filter((cartGroup) => cartGroup.carts.length);

          return {
            ...state,
            cartItemErrors,
            couponFetch: false,
            couponOffers,
            coupons,
            couponTerms,
            fetchPending: false,
            groupedCarts,
            lastUpdateTime: Date.now(),
            orgIdCartCount: Object.fromEntries(orgIdCartCount.entries()),
            pendingDeletes: {},
            pendingItemDeletes: {},
            pendingSkuUpdates: {},
            pendingUpdates: {},
            prizes: prizes || [],
            shippingAddress,
            unknownError: false,
          };
        },
        rejected: (state, action) => {
          const { meta: { requestId }, payload } = action;

          if (requestId !== state.groupedCartsRequestId) {
            return undefined;
          }

          const errorCodes = /** @type {any} */ (payload)?.response?.data?.errorCodes;
          if (errorCodes?.includes(ERROR.COUPON_INELIGIBLE)) {
            return {
              ...state,
              fetchPending: false,
              coupons: [],
              pendingDeletes: {},
              pendingUpdates: {},
              pendingItemDeletes: {},
              pendingSkuUpdates: {},
              couponFetch: true,
            };
          }
          return state;
        },
      },
    ),

    applyCoupons: create.asyncThunk(
      (/** @type {any} */ { groupKey, couponId, appliedCoupons }) => {
        let couponUse = (appliedCoupons || []).filter(({ key, couponId: id }) => (
          groupKey !== key && id !== couponId));
        if (couponId && groupKey) {
          couponUse = couponUse.concat({ key: groupKey, couponId });
        }
        return fetchGroupsWithCoupons({ coupons: couponUse }).then((res) => ({ res, couponUse }));
      },
      {
        pending: (state, action) => {
          const { groupKey, couponId, appliedCoupons } = action.meta.arg;
          let couponUse = (appliedCoupons || []).filter(({ key, couponId: id }) => (
            groupKey !== key && id !== couponId));
          if (couponId && groupKey) {
            couponUse = couponUse.concat({ key: groupKey, couponId });
          }
          state.prevCoupons = [...state.appliedCoupons];
          state.appliedCoupons = couponUse;
          state.prevSelectedPrizes = [...state.selectedPrizes];
          state.selectedPrizes = state.selectedPrizes.filter(({ key }) => key !== groupKey);
        },
        fulfilled: (state, action) => {
          state.groupedCarts = action.payload.res.data.carts;
          state.coupons = action.payload.res.data.coupons;
          state.appliedCoupons = action.payload.couponUse;
        },
        rejected: (state) => {
          state.appliedCoupons = state.prevCoupons;
          state.selectedPrizes = state.prevSelectedPrizes;
        },
      },
    ),

    applyPrize: create.asyncThunk(
      (/** @type {{ groupKey: string, prizeId: number }} */ { groupKey, prizeId }) =>
        fetchGroupsWithCoupons({ coupons: [{ key: groupKey, prizeId }] }),
      {
        pending: (state, action) => {
          const { groupKey, prizeId } = action.meta.arg;
          state.prevCoupons = [...state.appliedCoupons];
          state.appliedCoupons = state.appliedCoupons.filter(({ key }) => key !== groupKey);
          state.prevSelectedPrizes = [...state.selectedPrizes];
          let prizeUse = state.selectedPrizes.filter(({ key, prizeId: id }) => (
            groupKey !== key && id !== prizeId));
          if (prizeId && groupKey) {
            prizeUse = prizeUse.concat({ key: groupKey, prizeId });
          }
          state.selectedPrizes = prizeUse;
        },
        fulfilled: (state, action) => {
          state.groupedCarts = action.payload.data.carts;
          state.coupons = action.payload.data.coupons;
        },
        rejected: (state) => {
          state.appliedCoupons = state.prevCoupons;
          state.selectedPrizes = state.prevSelectedPrizes;
        },
      },
    ),

    removeCartItem: create.asyncThunk(
      (/** @type {{ cartId: string, cartItemId: string, storeId: string }} */ { cartItemId, storeId }) =>
        deleteCartItem(cartItemId, storeId),
      {
        pending: (state, action) => {
          const { cartId, cartItemId } = action.meta.arg;
          state.pendingDeletes = addOrUpdateObject({ ...state.pendingDeletes }, cartId, true);
          state.pendingItemDeletes = addOrUpdateObject(
            { ...state.pendingItemDeletes },
            cartId,
            { ...state.pendingItemDeletes?.[cartId], [cartItemId]: true },
          );
        },
        fulfilled: (state, action) => {
          const { cartId } = action.meta.arg;
          // empty body = server deleted the cart with its last item
          state.carts = addOrUpdateObject({ ...state.carts }, cartId, action.payload.data || null);
          state.pendingDeletes = addOrUpdateObject({ ...state.pendingDeletes }, cartId, false);
          state.pendingItemDeletes = addOrUpdateObject(
            { ...state.pendingItemDeletes },
            cartId,
            { ...state.pendingItemDeletes?.[cartId], [action.meta.arg.cartItemId]: false },
          );
        },
        rejected: (state, action) => {
          const { cartId, cartItemId } = action.meta.arg;
          state.pendingDeletes = addOrUpdateObject({ ...state.pendingDeletes }, cartId, false);
          state.pendingItemDeletes = addOrUpdateObject(
            { ...state.pendingItemDeletes },
            cartId,
            { ...state.pendingItemDeletes?.[cartId], [cartItemId]: false },
          );
        },
      },
    ),

    modifyCartItem: create.asyncThunk(
      (/** @type {{ cartId: string, quantity: number, sku: string, storeId: string }} */ { quantity, sku, storeId }) =>
        updateCartItem(quantity, sku, storeId),
      {
        pending: (state, action) => {
          const { cartId, sku } = action.meta.arg;
          state.pendingUpdates = addOrUpdateObject({ ...state.pendingUpdates }, cartId, true);
          state.pendingSkuUpdates = addOrUpdateObject(
            { ...state.pendingSkuUpdates },
            cartId,
            { ...state.pendingSkuUpdates[cartId], [sku]: true },
          );
        },
        fulfilled: (state, action) => {
          const { cartId, sku } = action.meta.arg;
          state.pendingUpdates = addOrUpdateObject({ ...state.pendingUpdates }, cartId, false);
          state.pendingSkuUpdates = addOrUpdateObject(
            { ...state.pendingSkuUpdates },
            cartId,
            { ...state.pendingSkuUpdates[cartId], [sku]: false },
          );
        },
        rejected: (state, action) => {
          const { cartId, sku } = action.meta.arg;
          state.pendingUpdates = addOrUpdateObject({ ...state.pendingUpdates }, cartId, false);
          state.pendingSkuUpdates = addOrUpdateObject(
            { ...state.pendingSkuUpdates },
            cartId,
            { ...state.pendingSkuUpdates[cartId], [sku]: false },
          );
        },
      },
    ),

    fetchSaveLater: create.asyncThunk(
      () => getSaveForLater(),
      {
        fulfilled: (state, action) => {
          const { items, errors } = action.payload.data;
          state.saved = { items, errors };
        },
      },
    ),

    addSaveLater: create.asyncThunk(
      (/** @type {{ quantity: number, sku: string, storeId: string }} */ { storeId, sku, quantity }) =>
        addSaveForLater(storeId, sku, quantity),
      {},
    ),

    deleteSaveLater: create.asyncThunk(
      (savedItemId) => deleteSaveForLater(savedItemId),
      {
        fulfilled: (state, action) => {
          const { items, errors } = action.payload.data;
          state.saved = { items, errors };
        },
      },
    ),

    moveToCart: create.asyncThunk(
      (/** @type {string} */ savedItemId) => moveSavedToCart(savedItemId),
      {
        fulfilled: (state, action) => {
          const { saveForLater, cart } = action.payload.data;
          let carts = { ...state.carts };
          let pendingUpdates = { ...state.pendingUpdates };
          if (cart?.id) {
            carts = addOrUpdateObject({ ...state.carts }, cart.id, cart);
            pendingUpdates = addOrUpdateObject({ ...state.pendingUpdates }, cart.id, true);
          }
          state.carts = carts;
          state.pendingUpdates = pendingUpdates;
          state.pendingCartAdd = cart?.id || null;
          state.saved = saveForLater;
        },
      },
    ),

    clearPendingCartAdd: create.reducer((state) => {
      state.pendingCartAdd = null;
    }),

    moveToSaved: create.asyncThunk(
      (/** @type {{ cartId: string, cartItemId: string }} */ { cartItemId }) =>
        moveCartToSaved(cartItemId),
      {
        pending: (state, action) => {
          const { cartId, cartItemId } = action.meta.arg;
          state.pendingDeletes = addOrUpdateObject({ ...state.pendingDeletes }, cartId, true);
          state.pendingItemDeletes = addOrUpdateObject(
            { ...state.pendingItemDeletes },
            cartId,
            { ...state.pendingItemDeletes?.[cartId], [cartItemId]: true },
          );
        },
        fulfilled: (state, action) => {
          const { cart, saveForLater } = action.payload.data;
          const { cartId, cartItemId } = action.meta.arg;
          state.carts = addOrUpdateObject({ ...state.carts }, cartId, cart);
          state.pendingDeletes = addOrUpdateObject({ ...state.pendingDeletes }, cartId, false);
          state.pendingItemDeletes = addOrUpdateObject(
            { ...state.pendingItemDeletes },
            cartId,
            { ...state.pendingItemDeletes?.[cartId], [cartItemId]: false },
          );
          state.saved = saveForLater;
        },
        rejected: (state, action) => {
          const { cartId, cartItemId } = action.meta.arg;
          state.pendingDeletes = addOrUpdateObject({ ...state.pendingDeletes }, cartId, false);
          state.pendingItemDeletes = addOrUpdateObject(
            { ...state.pendingItemDeletes },
            cartId,
            { ...state.pendingItemDeletes?.[cartId], [cartItemId]: false },
          );
        },
      },
    ),

    fetchCartsInformation: create.asyncThunk(
      (appliedCoupons) => fetchGroupsWithCoupons({ coupons: appliedCoupons }),
      {
        pending: (state) => { state.fetchPending = true; },
        fulfilled: (state, action) => {
          state.fetchPending = false;
          state.lastUpdateTime = Date.now();
          state.cartsInformation = action.payload.data;
          state.unknownError = false;
        },
        rejected: (state) => {
          state.fetchPending = false;
          state.lastUpdateTime = Date.now();
          state.unknownError = true;
        },
      },
    ),

    fetchCouponDetails: create.asyncThunk(
      (id) => getCoupon(id),
      {
        fulfilled: (state, action) => {
          state.couponTerms = {
            ...state.couponTerms,
            [action.payload.data.id || '0']: action.payload.data.termsConditions,
          };
        },
      },
    ),

    /**
     * Fetch shipInfo for cartGroups that have 2+ carts of the same org to determine SADness,
     * then let the reducer calculate freeShipInfos for all carts.
     */
    fetchAndDeriveFreeShipInfos: create.asyncThunk(
      (_, { getState }) => {
        const { cart: { cartFreeShipInfos, groupedCarts } } = /** @type {any} */ (getState());

        if (cartFreeShipInfos) {
          // Return an empty settled array so the fulfilled payload is always array-shaped
          return /** @type {Promise<PromiseSettledResult<any>[]>} */ (Promise.resolve([]));
        }

        /** @type {string[]} */
        const potentialSADGroups =
          groupedCarts?.reduce((potentialGroups, /** @type {CartGroupType} */ { carts, key }) => (
            Array.from((
              carts
                .reduce((orgCounts, { organizationId }) => (
                  orgCounts.set(organizationId, (orgCounts.get(organizationId) || 0) + 1)
                ), new Map())
                .values()
            )).some((count) => count > 1)
              ? [...potentialGroups, key]
              : potentialGroups
          ), []) || [];

        return Promise.allSettled(
          potentialSADGroups.map((cartGroupKey) => getCartSetShipInfo(cartGroupKey)),
        );
      },
      {
        fulfilled: (state, action) => {
          const { payload: shipInfoResponses } = action;
          const { groupedCarts } = state;
          let { sadCartGroups } = state;

          /** @type {Record<number, ProductTypes.FreeShipInfo>} */
          const cartFreeShipInfos = {};

          if (shipInfoResponses?.length) {
            const orgIdSADCarts = {};

            shipInfoResponses.forEach((response) => {
              /** @type {ShipInfoType} */
              const shipInfo = /** @type {PromiseFulfilledResult<any>} */ (response).value?.data;

              Object.entries(shipInfo?.options || {}).forEach(([orgIdStr, options]) => {
                options.forEach(({ cartIds }) => {
                  if (cartIds.length > 1) {
                    orgIdSADCarts[+orgIdStr] = [...orgIdSADCarts[+orgIdStr] || [], cartIds];
                  }
                });
              });
            });

            sadCartGroups = Object.values(orgIdSADCarts || {}).flat();
          }

          const allCarts = groupedCarts?.flatMap(({ carts }) => carts) || [];
          const cartsById = allCarts.reduce((byId, cart) => ({ ...byId, [cart.id]: cart }), {});

          allCarts.forEach((cart) => {
            if (cartFreeShipInfos[cart.id]) return;

            const sadGroup = sadCartGroups
              .find((ids) => ids.includes(cart.id))
              ?.map((id) => cartsById[id])
              .filter(Boolean);

            const carts = sadGroup || [cart];
            const freeShipInfo = calculateFreeShippingInfo({
              carts, shippingMethods: cart.shippingMethods,
            });

            carts.forEach(({ id }) => { cartFreeShipInfos[id] = freeShipInfo; });
          });

          state.cartFreeShipInfos = cartFreeShipInfos;
          state.sadCartGroups = sadCartGroups;
        },
      },
    ),
  }),

  extraReducers: (builder) => {
    // claimCouponOffer is defined after this slice to avoid a self-reference; match by type string
    builder.addCase('cart/coupon/claim/pending', (state, /** @type {any} */ action) => {
      const { couponOffer: { id: couponId } } = action.meta.arg;
      state.couponOffers = state.couponOffers
        .map((coupon) => (coupon.id === couponId ? { ...coupon, claimed: true } : coupon));
    });
    builder.addCase('cart/coupon/claim/rejected', (state, /** @type {any} */ action) => {
      const { couponOffer: { id: couponId } } = action.meta.arg;
      state.couponOffers = state.couponOffers
        .map((coupon) => (coupon.id === couponId ? { ...coupon, claimed: false } : coupon));
    });

    builder.addCase(fetchStoreCampaigns.fulfilled, (state, action) => {
      state.storesCampaignContents = action.payload.data;
    });
  },
});

export const {
  addSaveLater,
  applyCoupons,
  applyPrize,
  clearPendingCartAdd,
  deleteSaveLater,
  fetchAndDeriveFreeShipInfos,
  fetchCart,
  fetchCartsInformation,
  fetchCartSuggestions,
  fetchCartSuggestionsByProduct,
  fetchCouponDetails,
  fetchGroupedCarts,
  fetchSaveLater,
  fetchSecondaryMenu,
  fetchTrendingSearches,
  modifyCartItem,
  moveToCart,
  moveToSaved,
  removeCartItem,
} = cartSlice.actions;

// Defined after slice exports so they can reference the action creators above without TDZ issues
export const claimCouponOffer = createAsyncThunk('cart/coupon/claim',
  (/** @type {any} */ { cartId, couponOffer }, { dispatch, getState }) =>
    claimCoupon({ couponId: couponOffer.id })
      .then((/** @type {any} */ { data: coupon }) => {
        const { cart: { appliedCoupons, groupedCarts } } = /** @type {any} */ (getState());

        const cartGroup = groupedCarts
          .find((group) => group.carts.some((cart) => cart.id === cartId));

        const shouldApplyCoupon = couponOffer.carts
          .some((cart) => cart.ready && cart.key === cartGroup.key);

        if (shouldApplyCoupon) {
          dispatch(applyCoupons({
            groupKey: cartGroup.key,
            couponId: coupon.id,
            appliedCoupons,
          }));
        } else {
          dispatch(fetchGroupedCarts(appliedCoupons));
        }
      }));

export const refreshCarts = createAsyncThunk('cart/refresh',
  (_, { dispatch, getState }) => (
    dispatch(fetchGroupedCarts((/** @type {any} */ (getState())).cart.appliedCoupons))
  ));

export const CartActionsDispatch = (dispatch) => ({
  ...bindActionCreators({
    claimCouponOffer,
    clearPendingCartAdd,
    fetchAndDeriveFreeShipInfos,
    fetchCart,
    fetchCouponDetails,
    fetchGroupedCarts,
    refreshCarts,
  }, dispatch),
  // Adapters: thunks now take a single object arg; these wrappers preserve the old positional API
  applyCoupons: (groupKey, couponId, appliedCoupons) =>
    dispatch(applyCoupons({ groupKey, couponId, appliedCoupons })),
  applyPrize: (groupKey, prizeId) => dispatch(applyPrize({ groupKey, prizeId })),
  modifyCartItem: (quantity, sku, cartId, storeId) =>
    dispatch(modifyCartItem({ quantity, sku, cartId, storeId })),
  removeCartItem: (cartItemId, cartId, storeId) =>
    dispatch(removeCartItem({ cartItemId, cartId, storeId })),
});

export const SaveLaterActionsDispatch = (dispatch) => ({
  ...bindActionCreators({
    addSaveLater,
    deleteSaveLater,
    fetchPromoStores,
    fetchSaveLater,
    moveToCart,
  }, dispatch),
  // moveToSaved now takes a single object arg; adapter preserves the old positional API
  moveToSaved: (cartItemId, cartId) => dispatch(moveToSaved({ cartItemId, cartId })),
});

export const CartsInformationActionsDispatch = (dispatch) => bindActionCreators({
  fetchCartsInfo: fetchCartsInformation,
}, dispatch);

export default cartSlice.reducer;
